/**
 * Automation Labs Command Prompt Frontend
 * Terminal-style interface for controlling 105+ AI agents
 */

import React, { useState, useEffect, useRef } from 'react';
import { Typography, Input, Space, Tag, Button, Upload, Spin, message } from 'antd';
import { 
  SendOutlined, 
  UploadOutlined, 
  ClearOutlined,
  HistoryOutlined,
  RobotOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './App.css';

const { Text, Title } = Typography;

interface Message {
  id: string;
  type: 'user' | 'assistant' | 'system' | 'error';
  content: string;
  timestamp: Date;
  agent?: string;
  progress?: number;
}

interface StreamEvent {
  type: string;
  data: any;
  agent?: string;
  progress?: number;
  timestamp: string;
}

function App() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [capabilities, setCapabilities] = useState<any>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<any>(null);

  // Initialize session on mount
  useEffect(() => {
    initializeSession();
    fetchCapabilities();
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const initializeSession = async () => {
    try {
      const response = await fetch('/api/session/create', {
        method: 'POST'
      });
      const data = await response.json();
      setSessionId(data.sessionId);
      
      // Add welcome message
      addMessage({
        type: 'system',
        content: '🚀 Welcome to Automation Labs Command Prompt!\n\nYou can control 105+ AI agents across all labs. Try commands like:\n- "Generate a React component for user authentication"\n- "Analyze the security of my Docker configuration"\n- "Create an invoice processing system"\n- "Help me build a mobile app"\n\nType "help" for more information.'
      });
    } catch (error) {
      console.error('Failed to initialize session:', error);
    }
  };

  const fetchCapabilities = async () => {
    try {
      const response = await fetch('/api/capabilities', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        }
      });
      const data = await response.json();
      setCapabilities(data);
    } catch (error) {
      console.error('Failed to fetch capabilities:', error);
    }
  };

  const addMessage = (message: Partial<Message>) => {
    const newMessage: Message = {
      id: crypto.randomUUID(),
      timestamp: new Date(),
      ...message
    } as Message;
    
    setMessages(prev => [...prev, newMessage]);
  };

  const handleCommand = async () => {
    if (!input.trim() || loading) return;

    const command = input.trim();
    setInput('');
    setCommandHistory(prev => [...prev, command]);
    setHistoryIndex(-1);

    // Add user message
    addMessage({
      type: 'user',
      content: command
    });

    // Handle special commands
    if (command.toLowerCase() === 'help') {
      showHelp();
      return;
    }

    if (command.toLowerCase() === 'clear') {
      setMessages([]);
      return;
    }

    if (command.toLowerCase() === 'capabilities') {
      showCapabilities();
      return;
    }

    // Process command through API
    setLoading(true);
    const messageId = crypto.randomUUID();

    try {
      const response = await fetch('/api/command', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
          'X-Session-ID': sessionId || ''
        },
        body: JSON.stringify({
          command,
          context: {
            previousMessages: messages.slice(-5)
          },
          stream: true
        })
      });

      if (!response.ok) {
        throw new Error('Command failed');
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentMessage = '';

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            try {
              const event: StreamEvent = JSON.parse(data);
              
              switch (event.type) {
                case 'thinking':
                case 'executing':
                  addMessage({
                    id: messageId,
                    type: 'system',
                    content: event.data,
                    agent: event.agent
                  });
                  break;
                
                case 'progress':
                  // Update progress indicator
                  setMessages(prev => prev.map(msg => 
                    msg.id === messageId 
                      ? { ...msg, progress: event.progress }
                      : msg
                  ));
                  break;
                
                case 'result':
                  currentMessage += event.data + '\n';
                  setMessages(prev => {
                    const filtered = prev.filter(msg => msg.id !== messageId);
                    return [...filtered, {
                      id: messageId,
                      type: 'assistant',
                      content: currentMessage,
                      timestamp: new Date(),
                      agent: event.agent
                    }];
                  });
                  break;
                
                case 'error':
                  addMessage({
                    type: 'error',
                    content: event.data,
                    agent: event.agent
                  });
                  break;
                
                case 'done':
                  // Command completed
                  break;
              }
            } catch (e) {
              console.error('Failed to parse SSE event:', e);
            }
          }
        }
      }
    } catch (error) {
      addMessage({
        type: 'error',
        content: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCommand();
    }
    
    // Command history navigation
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (historyIndex < commandHistory.length - 1) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex]);
      }
    }
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInput('');
      }
    }
  };

  const handleFileUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sessionId', sessionId || '');

    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: formData
      });

      await response.json();
      message.success(`File uploaded: ${file.name}`);
      
      // Add file context to next command
      addMessage({
        type: 'system',
        content: `📎 File uploaded: ${file.name} (${formatFileSize(file.size)})`
      });
    } catch (error) {
      message.error('Failed to upload file');
    }

    return false; // Prevent default upload
  };

  const showHelp = () => {
    addMessage({
      type: 'assistant',
      content: `# Automation Labs Command Prompt Help

## Available Commands:
- **help** - Show this help message
- **clear** - Clear the terminal
- **capabilities** - Show available agents and labs

## Example Commands:
### Code Generation
- "Generate a React component for user authentication"
- "Create a Python API with FastAPI for invoice processing"
- "Build a mobile app for task management"

### Data Analysis
- "Analyze sales data and create visualizations"
- "Generate insights from customer feedback"

### Automation
- "Create a workflow to process invoices automatically"
- "Build a chatbot for customer support"

### Security
- "Scan my Docker configuration for vulnerabilities"
- "Review this code for security issues"

### Content Creation
- "Write a blog post about AI trends"
- "Create social media content for product launch"

## Tips:
- Upload files for context using the upload button
- Use arrow keys to navigate command history
- Commands are processed by multiple specialized agents
- Results include confidence scores and agent contributions`
    });
  };

  const showCapabilities = () => {
    if (!capabilities) return;
    
    addMessage({
      type: 'assistant',
      content: `# Automation Labs Capabilities

## Stats:
- **Total Agents**: ${capabilities.totalAgents}
- **Departments**: ${capabilities.departments.join(', ')}
- **Active Labs**: ${capabilities.labs.length}

## Categories:
${capabilities.categories.map((cat: any) => `- ${cat}`).join('\n')}

## Features:
${capabilities.features.map((feat: any) => `- ${feat}`).join('\n')}

## Available Labs:
${capabilities.labs.map((lab: any) => `- **${lab}**`).join('\n')}`
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderMessage = (message: Message) => {
    const isMarkdown = message.type === 'assistant' || message.type === 'system';
    
    return (
      <div className={`message message-${message.type}`}>
        <div className="message-header">
          <Space>
            {message.type === 'user' && <Text type="secondary">$</Text>}
            {message.type === 'assistant' && <RobotOutlined />}
            {message.type === 'system' && <CodeOutlined />}
            {message.agent && <Tag color="blue">{message.agent}</Tag>}
            <Text type="secondary" className="timestamp">
              {message.timestamp.toLocaleTimeString()}
            </Text>
          </Space>
          {message.progress && (
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${message.progress}%` }}
              />
            </div>
          )}
        </div>
        <div className="message-content">
          {isMarkdown ? (
            <ReactMarkdown
              components={{
                code({inline, className, children, ...props}: any) {
                  const match = /language-(\w+)/.exec(className || '');
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={vscDarkPlus as any}
                      language={match[1]}
                      PreTag="div"
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                }
              }}
            >
              {message.content}
            </ReactMarkdown>
          ) : (
            <pre>{message.content}</pre>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="app">
      <div className="header">
        <Title level={3} style={{ margin: 0, color: '#fff' }}>
          <Space>
            <RobotOutlined />
            Automation Labs Command Prompt
          </Space>
        </Title>
        <Space>
          {capabilities && (
            <Text style={{ color: '#fff' }}>
              {capabilities.totalAgents} agents • {capabilities.labs.length} labs
            </Text>
          )}
          <Button 
            icon={<HistoryOutlined />} 
            onClick={() => showCapabilities()}
          >
            Capabilities
          </Button>
          <Button 
            icon={<ClearOutlined />} 
            onClick={() => setMessages([])}
          >
            Clear
          </Button>
        </Space>
      </div>
      
      <div className="terminal">
        <div className="messages">
          {messages.map(message => (
            <div key={message.id}>
              {renderMessage(message)}
            </div>
          ))}
          {loading && (
            <div className="message message-system">
              <Space>
                <Spin size="small" />
                <Text type="secondary">Processing command...</Text>
              </Space>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        <div className="input-area">
          <Space.Compact style={{ width: '100%' }}>
            <Upload
              beforeUpload={handleFileUpload}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />} />
            </Upload>
            <Input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter command..."
              disabled={loading}
              style={{ fontFamily: 'monospace' }}
              suffix={
                <Button
                  type="text"
                  icon={<SendOutlined />}
                  onClick={handleCommand}
                  disabled={loading || !input.trim()}
                />
              }
            />
          </Space.Compact>
        </div>
      </div>
    </div>
  );
}

export default App;